import { useCallback, useMemo } from "react";
import { buildOrderBOM } from "@/lib/orderBom";
import { UNIT_TO_BASE } from "@/lib/bom";

const invUnitOfT = (t) => {
  const rct = t?.roll_content_type;
  if ((rct === "ft" || rct === "m") && Number(t?.content_per_stock_unit) > 0) return rct;
  return t?.roll_weight_unit || "";
};

// Truckload Coverage view-model: converts inventory chart data into truckloads supported.
// No selection = average across every valid product × pack config that uses the component.
export function useCoverage({ viewCalc, products, covProductId, covCfgId, components, groups, labelData, componentData, yMax }) {
  const covProduct = products.find((pp) => pp.id === covProductId) || null;
  const covCfgs = covProduct?.pack_configs || [];
  const covCfg = covCfgs.find((c) => c.id === covCfgId) || null;
  const covSpecific = !!(covProduct && covCfg);

  const covMapsList = useMemo(() => {
    const pairs = [];
    products.forEach((pp) => {
      if (covProductId && pp.id !== covProductId) return;
      (pp.pack_configs || []).forEach((cfg) => {
        if (covProductId && covCfgId && cfg.id !== covCfgId) return;
        const cps = Number(cfg.bottles_per_case) || 0;
        const cpp = Number(cfg.cases_per_pallet) || 0;
        const ppt = Number(cfg.pallets_per_truckload) || 0;
        if (!cps || !cpp || !ppt) return;
        const rows = buildOrderBOM(pp, components, { cfg, casePackSize: cps, totalCases: cpp * ppt, totalPallets: ppt, totalTrucks: 1, designId: "" });
        const byType = {};
        const byComp = {};
        rows.forEach((r) => {
          if (!r.required) return;
          let req = r.required;
          if (r.unit && r.unit !== "count") {
            const comp = components.find((c) => c.id === r.component_id);
            const t = (comp?.types || []).find((x) => x.id === r.type_id) || comp?.types?.[0];
            req = req / (UNIT_TO_BASE[invUnitOfT(t)] || 1);
          }
          if (r.type_id) byType[r.type_id] = (byType[r.type_id] || 0) + req;
          else byComp[r.component_id] = (byComp[r.component_id] || 0) + req;
        });
        pairs.push({ label: `${pp.name} · ${cfg.label || `${cps} Pack`}`, byType, byComp, cpp, ppt });
      });
    });
    return pairs;
  }, [products, covProductId, covCfgId, components]);

  const toCoverage = useCallback((d, pool) => {
    const baseTypeId = String(d.typeId).split("::")[0];
    const availPool = Math.max(0, pool ? pool.sum : d.avail);
    const covRows = [];
    covMapsList.forEach((m) => {
      const req = m.byType[baseTypeId] ?? m.byComp[d.compId];
      if (req) covRows.push({ label: m.label, req: Math.round(req * 100) / 100, cov: Math.round((availPool / req) * 100) / 100, ppt: m.ppt, cpt: m.cpp * m.ppt });
    });
    const base = { ...d, covMode: true, covScope: covSpecific ? "specific" : covProductId ? "product" : "all", realAvail: d.avail, covShared: !!pool && pool.count > 1 };
    if (!covRows.length) return { ...base, covRows: [], covAvg: false, cov: null, reqPerTruck: null, avail: 0, reserved: 0 };
    const cov = Math.round((covRows.reduce((s, r) => s + r.cov, 0) / covRows.length) * 100) / 100;
    return { ...base, covRows, covAvg: covRows.length > 1, cov, reqPerTruck: covRows.length === 1 ? covRows[0].req : null, avail: cov, reserved: 0 };
  }, [covMapsList, covSpecific, covProductId]);

  const labelPools = useMemo(() => {
    const m = {};
    labelData.forEach((d) => {
      const k = d.typeId.split("::")[0];
      const e = m[k] || (m[k] = { sum: 0, count: 0 });
      e.sum += Math.max(0, d.avail);
      e.count += 1;
    });
    return m;
  }, [labelData]);

  const coverage = viewCalc === "coverage";
  const displayGroups = useMemo(() => (coverage ? groups.map((g) => ({ ...g, variants: g.variants.map((v) => toCoverage({ ...v, compId: g.comp.id, compName: g.comp.name })) })) : groups), [coverage, groups, toCoverage]);
  const displayLabelData = useMemo(() => (coverage ? labelData.map((d) => toCoverage(d, labelPools[d.typeId.split("::")[0]])) : labelData), [coverage, labelData, toCoverage, labelPools]);
  const displayComponentData = useMemo(() => (coverage ? componentData.map((d) => toCoverage(d)) : componentData), [coverage, componentData, toCoverage]);
  const displayYMax = useMemo(() => {
    if (!coverage) return yMax;
    const m = Math.max(0, ...displayComponentData.map((d) => d.avail));
    return m <= 0 ? 10 : m;
  }, [coverage, yMax, displayComponentData]);

  return { coverage, covProduct, covCfgs, covCfg, covSpecific, displayGroups, displayLabelData, displayComponentData, displayYMax, toCoverage };
}
